import { MapInterval } from "@/engine/engine";
import { constructInterval } from "./intervals";
import { HGNow } from "./types";

// Mirrors frac::PartOffset in hg_cpp's its/frac.h: a parent holding `total`
// child units splits into `k` parts of whole units, with the remainder going to
// the earliest parts (a 31-day month halves into 16 + 15 days, a century thirds
// into 34 + 33 + 33 years). Returns the 0-based part holding child unit
// `offset`.
function fractionIndex(total: number, k: number, offset: number): number {
  const size = Math.floor(total / k);
  const remainder = total % k;
  const longPartsEnd = remainder * (size + 1);
  return offset < longPartsEnd
    ? Math.floor(offset / (size + 1))
    : remainder + Math.floor((offset - longPartsEnd) / size);
}

export function calculateNow() {
  const nowIntervals: HGNow = {
    byr: 1n,
    byr_q: 1n,
    byr_h: 1n,
    myr: 1n,
    myr_q: 1n,
    myr_h: 1n,
    kyr: 3n,
  };
  const currentDate = new Date();
  const currentYear = BigInt(currentDate.getFullYear());
  nowIntervals["y"] = currentYear;
  nowIntervals["dec"] = (currentYear - 1n) / 10n + 1n;

  // c/kyr/myr/byr fraction types group whole years, so they're cheap
  // (currentYear - 1) / groupSize + 1 lookups, matching their engine-side
  // grouping (its/*_frac.cpp's FracDaysY<Years>) — no wasm round-trip.
  nowIntervals["c"] = (currentYear - 1n) / 100n + 1n;
  nowIntervals["c_q"] = (currentYear - 1n) / 25n + 1n;
  nowIntervals["c_h"] = (currentYear - 1n) / 50n + 1n;
  nowIntervals["kyr_q"] = (currentYear - 1n) / 250n + 1n;
  nowIntervals["kyr_h"] = (currentYear - 1n) / 500n + 1n;
  nowIntervals["myr_q"] = (currentYear - 1n) / 250000n + 1n;
  nowIntervals["myr_h"] = (currentYear - 1n) / 500000n + 1n;
  nowIntervals["byr_q"] = (currentYear - 1n) / 250000000n + 1n;
  nowIntervals["byr_h"] = (currentYear - 1n) / 500000000n + 1n;

  // The thirds don't divide their parent evenly (a century is 34 + 33 + 33
  // years, a millennium 34 + 33 + 33 decades, a myr/byr 334 + 333 + 333 of the
  // next unit down), so they need the same remainder-to-the-front split the
  // engine uses rather than a plain division.
  const yearsElapsed = currentYear - 1n;
  nowIntervals["c_t"] =
    (yearsElapsed / 100n) * 3n +
    BigInt(fractionIndex(100, 3, Number(yearsElapsed % 100n))) +
    1n;
  nowIntervals["kyr_t"] =
    (yearsElapsed / 1000n) * 3n +
    BigInt(fractionIndex(100, 3, Number((yearsElapsed / 10n) % 100n))) +
    1n;
  nowIntervals["myr_t"] =
    (yearsElapsed / 1000000n) * 3n +
    BigInt(fractionIndex(1000, 3, Number((yearsElapsed / 1000n) % 1000n))) +
    1n;
  nowIntervals["byr_t"] =
    (yearsElapsed / 1000000000n) * 3n +
    BigInt(fractionIndex(1000, 3, Number((yearsElapsed / 1000000n) % 1000n))) +
    1n;

  const months = MapInterval(constructInterval(currentYear, "y"), "m");
  const currentMonth = months.begin.n + BigInt(currentDate.getMonth());
  nowIntervals["m"] = currentMonth;

  // y_q/y_t/y_h group whole (absolute) months, same grouping shape as
  // its/y_frac.cpp's FracDays<Months>.
  nowIntervals["y_q"] = (currentMonth - 1n) / 3n + 1n;
  nowIntervals["y_t"] = (currentMonth - 1n) / 4n + 1n;
  nowIntervals["y_h"] = (currentMonth - 1n) / 6n + 1n;

  const days = MapInterval(constructInterval(currentMonth, "m"), "d");
  const currentDay = days.begin.n + BigInt(currentDate.getDate() - 1);
  nowIntervals["d"] = currentDay;
  nowIntervals["w"] = (currentDay - 1n) / 7n + 1n;

  // m_t/m_h subdivide the current month into whole days, so which part "now"
  // falls in depends on the month's length (see its/m_frac.cpp).
  const daysInCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  ).getDate();
  const dayOfMonth = currentDate.getDate() - 1;
  nowIntervals["m_t"] =
    (currentMonth - 1n) * 3n +
    BigInt(fractionIndex(daysInCurrentMonth, 3, dayOfMonth)) +
    1n;
  nowIntervals["m_h"] =
    (currentMonth - 1n) * 2n +
    BigInt(fractionIndex(daysInCurrentMonth, 2, dayOfMonth)) +
    1n;

  // d_q/d_t/d_h subdivide the current day by hour.
  nowIntervals["d_q"] =
    (currentDay - 1n) * 4n +
    BigInt(Math.floor(currentDate.getHours() / 6)) +
    1n;
  nowIntervals["d_t"] =
    (currentDay - 1n) * 3n +
    BigInt(Math.floor(currentDate.getHours() / 8)) +
    1n;
  nowIntervals["d_h"] =
    (currentDay - 1n) * 2n +
    BigInt(Math.floor(currentDate.getHours() / 12)) +
    1n;

  const hours = MapInterval(constructInterval(currentDay, "d"), "h");
  const currentHour = hours.begin.n + BigInt(currentDate.getHours());
  nowIntervals["h"] = currentHour;

  // h_q/h_t/h_h subdivide the current hour by minute.
  nowIntervals["h_q"] =
    (currentHour - 1n) * 4n +
    BigInt(Math.floor(currentDate.getMinutes() / 15)) +
    1n;
  nowIntervals["h_t"] =
    (currentHour - 1n) * 3n +
    BigInt(Math.floor(currentDate.getMinutes() / 20)) +
    1n;
  nowIntervals["h_h"] =
    (currentHour - 1n) * 2n +
    BigInt(Math.floor(currentDate.getMinutes() / 30)) +
    1n;

  const minutes = MapInterval(constructInterval(currentHour, "h"), "min");
  const currentMinute = minutes.begin.n + BigInt(currentDate.getMinutes());
  nowIntervals["min"] = currentMinute;
  const seconds = MapInterval(constructInterval(currentMinute, "min"), "s");
  const currentSecond = seconds.begin.n + BigInt(currentDate.getSeconds());
  nowIntervals["s"] = currentSecond;

  return nowIntervals;
}
